import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("anchor_admin_token", data.token);
      localStorage.setItem("anchor_admin_email", data.email);
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-view"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] hover:text-[#C07865] transition-colors mb-12"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Return home
        </Link>

        <p className="overline mb-6">Admin</p>
        <h1 className="serif text-4xl md:text-5xl text-[#15243D] leading-[1.05] mb-10" style={{ fontWeight: 300 }}>
          Quiet door.
        </h1>

        <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="anchor-input"
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="anchor-input"
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            data-testid="admin-login-button"
          >
            {loading ? "Signing in…" : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function Empty({ label }) {
  return (
    <p
      className="text-sm font-light text-[#8A8D93] py-12 text-center border border-dashed border-[#15243D]/10"
      data-testid={`empty-${label}`}
    >
      No {label} yet.
    </p>
  );
}

function Dashboard({ email, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }}>
      <header className="border-b border-[#15243D]/10 sticky top-0 bg-[#F7F5F0]/90 backdrop-blur-md z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="admin-brand-link"
              className="serif text-lg tracking-[0.3em] text-[#15243D]"
              style={{ fontWeight: 400 }}
            >
              ANCHOR
            </Link>
            <span className="text-xs tracking-[0.2em] uppercase text-[#8A8D93]">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-xs font-light text-[#5A5D63] hidden sm:inline">
              {email}
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] hover:text-[#C07865] transition-colors"
            >
              Logout <LogOut size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <p className="overline mb-4">Submissions</p>
        <h1 className="serif text-4xl md:text-5xl text-[#15243D] leading-[1.05] mb-12" style={{ fontWeight: 300 }}>
          What has arrived.
        </h1>

        {loading && (
          <p className="text-sm font-light text-[#8A8D93]">Loading…</p>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#15243D]/10 mb-12">
              {[
                ["Applications", data.counts.coaching_applications],
                ["Agreements", data.counts.agreements],
                ["Program leads", data.counts.program_leads],
                ["Contact messages", data.counts.contacts],
              ].map(([label, count]) => (
                <div
                  key={label}
                  className="bg-[#F7F5F0] p-6 md:p-8"
                  data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <p className="text-xs tracking-[0.18em] uppercase text-[#8A8D93] mb-3">
                    {label}
                  </p>
                  <p className="serif text-4xl text-[#15243D]" style={{ fontWeight: 300 }}>
                    {count}
                  </p>
                </div>
              ))}
            </div>

            <Tabs defaultValue="applications" className="w-full" data-testid="admin-tabs">
              <TabsList className="bg-transparent border-b border-[#15243D]/10 rounded-none w-full justify-start gap-2 mb-8 p-0">
                {[
                  ["applications", "Coaching Applications"],
                  ["agreements", "Agreements"],
                  ["leads", "Program Leads"],
                  ["contacts", "Contact Messages"],
                ].map(([v, l]) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    data-testid={`admin-tab-${v}`}
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#C07865] data-[state=active]:bg-transparent data-[state=active]:text-[#C07865] data-[state=active]:shadow-none px-4 py-3 text-xs tracking-[0.15em] uppercase font-light text-[#5A5D63]"
                  >
                    {l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="applications">
                {data.coaching_applications.length === 0 ? (
                  <Empty label="applications" />
                ) : (
                  <div className="space-y-6">
                    {data.coaching_applications.map((a) => (
                      <article
                        key={a.id}
                        className="bg-white p-6 md:p-8 border border-[#15243D]/5"
                        data-testid={`application-card-${a.id}`}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
                          <h3 className="serif text-xl text-[#15243D]" style={{ fontWeight: 300 }}>
                            {a.name}
                          </h3>
                          <p className="text-xs tracking-[0.18em] uppercase text-[#8A8D93]">
                            {formatDate(a.created_at)}
                          </p>
                        </div>
                        <p className="text-sm font-light text-[#5A5D63] mb-1">
                          {a.email} {a.phone && `· ${a.phone}`}
                        </p>
                        <p className="text-xs tracking-[0.18em] uppercase text-[#C07865] mb-6">
                          Commitment · {a.commitment_level}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm font-light text-[#15243D]">
                          <div>
                            <p className="text-xs tracking-[0.15em] uppercase text-[#8A8D93] mb-2">
                              Current
                            </p>
                            <p className="leading-relaxed text-[#5A5D63]">{a.current_challenges}</p>
                          </div>
                          <div>
                            <p className="text-xs tracking-[0.15em] uppercase text-[#8A8D93] mb-2">
                              Goals
                            </p>
                            <p className="leading-relaxed text-[#5A5D63]">{a.goals}</p>
                          </div>
                          <div>
                            <p className="text-xs tracking-[0.15em] uppercase text-[#8A8D93] mb-2">
                              Why now
                            </p>
                            <p className="leading-relaxed text-[#5A5D63]">{a.why_now}</p>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="agreements">
                {data.agreements.length === 0 ? (
                  <Empty label="agreements" />
                ) : (
                  <div className="space-y-4">
                    {data.agreements.map((a) => (
                      <div
                        key={a.id}
                        className="bg-white p-6 border border-[#15243D]/5 flex flex-wrap items-baseline justify-between gap-4"
                        data-testid={`agreement-row-${a.id}`}
                      >
                        <div>
                          <p className="serif text-lg text-[#15243D]" style={{ fontWeight: 300 }}>
                            {a.full_name}
                          </p>
                          <p className="text-sm font-light text-[#5A5D63]">{a.email}</p>
                          <p className="text-xs italic text-[#8A8D93] mt-1">signed: {a.signature}</p>
                        </div>
                        <p className="text-xs tracking-[0.18em] uppercase text-[#8A8D93]">
                          {formatDate(a.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="leads">
                {data.program_leads.length === 0 ? (
                  <Empty label="leads" />
                ) : (
                  <div className="space-y-2">
                    {data.program_leads.map((l) => (
                      <div
                        key={l.id}
                        className="bg-white p-4 border border-[#15243D]/5 flex flex-wrap items-baseline justify-between gap-4"
                        data-testid={`lead-row-${l.id}`}
                      >
                        <div>
                          <p className="text-sm font-light text-[#15243D]">
                            {l.email}
                          </p>
                          {l.name && (
                            <p className="text-xs text-[#8A8D93]">{l.name}</p>
                          )}
                        </div>
                        <p className="text-xs tracking-[0.18em] uppercase text-[#8A8D93]">
                          {formatDate(l.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="contacts">
                {data.contacts.length === 0 ? (
                  <Empty label="messages" />
                ) : (
                  <div className="space-y-6">
                    {data.contacts.map((c) => (
                      <article
                        key={c.id}
                        className="bg-white p-6 border border-[#15243D]/5"
                        data-testid={`contact-row-${c.id}`}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
                          <h3 className="serif text-lg text-[#15243D]" style={{ fontWeight: 300 }}>
                            {c.name}{" "}
                            <span className="text-sm font-light text-[#5A5D63]">
                              · {c.email}
                            </span>
                          </h3>
                          <p className="text-xs tracking-[0.18em] uppercase text-[#8A8D93]">
                            {formatDate(c.created_at)}
                          </p>
                        </div>
                        <p className="text-sm font-light leading-relaxed text-[#5A5D63] whitespace-pre-wrap">
                          {c.message}
                        </p>
                      </article>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("anchor_admin_token");
    const email = localStorage.getItem("anchor_admin_email");
    return token && email ? { token, email } : null;
  });

  const logout = () => {
    localStorage.removeItem("anchor_admin_token");
    localStorage.removeItem("anchor_admin_email");
    setAuth(null);
  };

  if (!auth) return <LoginView onLogin={setAuth} />;
  return <Dashboard email={auth.email} onLogout={logout} />;
}
